import { Directive, HostBinding, Input, OnDestroy, OnInit, Optional, Renderer2 } from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NgControl,
  NgForm
} from '@angular/forms';
import { Nullable } from 'rt-shared-symbols';
import { BehaviorSubject, combineLatest, map, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  extractCombinedStatusesFromFormControl,
  FormControlCombinedStatuses,
  formControlCombinedStatusesAdapter
} from '../symbols/rt-inputs-control-status-adapter.symbols';
import { RtValidationErrorHandleStrategy } from '../symbols/rt-inputs-types.symbols';
import {
  rtFormsDefineInvalidityObservable,
  rtFormsOverwriteNgControlResetFunction
} from '../symbols/rt-inputs-validity.symbols';

@Directive({
  standalone: true
})
export abstract class RtInputsSwitchDirective implements OnInit, OnDestroy, ControlValueAccessor {

  /** Relation between the error name imposed by Validators and the error message. */
  @Input() validationMessages: Nullable<{ [key: string]: string }>;

  /** Error handle strategy. */
  @Input() errorHandleStrategy: RtValidationErrorHandleStrategy = RtValidationErrorHandleStrategy.afterLeft;

  @Input() backgroundColor: string = 'var(--primary-500)';

  isChanged$ = new BehaviorSubject<boolean>(null);

  /** Triggered whether the value is changed. Will be overwritten by Angular forms. */
  public onChange?: (value: boolean) => void;

  /** Triggered whether the control is touched. Will be overwritten by Angular forms. */
  public onTouched?: () => void;

  /** Indicates whether the validation should be displayed. */
  public shouldIndicateInvalidity$: Observable<boolean>;

  /** Observable with the combined statuses of the form control. */
  public providedControlStatuses$: Observable<FormControlCombinedStatuses>;

  /** Indicates whether the control is submitted. */
  public readonly isControlSubmitted$ = new BehaviorSubject(false);

  /** Form control for the input field. */
  public readonly toggleController = new FormControl<boolean>(false);

  /** Component subscriptions. Will be unsubscribed on component destroy. */
  public readonly subscription = new Subscription();

  @HostBinding('class.disabled') protected isDisabled = false;

  constructor(
    protected renderer: Renderer2,
    public ngControl: NgControl,
    @Optional() public controlContainer: ControlContainer
  ) {
    // Use the `ngControl` to access the related form control.
    ngControl.valueAccessor = this;

    // Observe the form submit event if the related strategy is set.
    if (!controlContainer) {
      throw new Error(
        `The controlContainer is not found.
           It is impossible to show validation on form submit event if no form is provided.
           The ${RtInputsSwitchDirective.name} should be used within a <form>.
           If this is intended, change the errorHandleStrategy to the different value.`
      );
    }

    // Observe the form submit event to update the submitted state.
    if (controlContainer instanceof NgForm || controlContainer instanceof FormGroupDirective) {
      this.subscription.add(
        controlContainer.ngSubmit.subscribe(() => {
          this.isControlSubmitted$.next(true);
        })
      );
    }
  }

  ngOnInit(): void {
    // Overwrite the reset function of the control to reset the submitted state.
    rtFormsOverwriteNgControlResetFunction(this.ngControl, this.isControlSubmitted$);

    // Define the single event object based on the form control events.
    if (this.ngControl?.control?.events) {
      this.providedControlStatuses$ = this.ngControl.control.events.pipe(
        formControlCombinedStatusesAdapter(extractCombinedStatusesFromFormControl(this.ngControl.control))
      );
    }


    // Determine whether the control should be displayed as invalid.
    this.shouldIndicateInvalidity$ = rtFormsDefineInvalidityObservable(
      this.providedControlStatuses$,
      this.isControlSubmitted$,
      this.errorHandleStrategy
    );

    // Update the control statuses based on the provided statuses.
    this.subscription.add(
      this.providedControlStatuses$.subscribe(status => {
        const currentControlState = extractCombinedStatusesFromFormControl(this.toggleController);
        // If touched status is not synced, update the control status.
        if (status.touched !== currentControlState.touched) {
          if (status.touched) {
            this.toggleController.markAsTouched();
          } else {
            this.toggleController.markAsUntouched();
          }
        }

        // If pristine status is not synced, update the control status.
        if (status.pristine !== currentControlState.pristine) {
          if (status.pristine) {
            this.toggleController.markAsPristine();
          } else {
            this.toggleController.markAsDirty();
          }
        }
      })
    );

    // Notify the value accessor about the touch events in the input field.
    this.subscription.add(
      this.toggleController.events
        .pipe(
          // Cast the event to the Partial<FormControlCombinedStatuses> type as there is no way to cast it to the exact type.
          map(event => event as unknown as Partial<FormControlCombinedStatuses>),

          // Filter the statuses that are touched.
          filter(statuses => !!statuses?.touched)
        )
        .subscribe(() => {
          // Notify the value accessor about the touch events in the input field.
          if (this.onTouched) {
            this.onTouched();
          }
        })
    );

    // Notify the value accessor about the changes in the input field.
    this.subscription.add(
      this.toggleController.valueChanges.subscribe(value => {
        // Notify the value accessor about the changes in the input field.
        this.isChanged$.next(true);
        if (this.onChange) {
          this.onChange(value);
        }
      })
    );

    this.subscription.add(
      combineLatest([this.shouldIndicateInvalidity$, this.toggleController.valueChanges]).subscribe(([isInvalid]) => {
        if (isInvalid) {
          this.toggleController.setErrors({ invalid: true });
        } else {
          this.toggleController.setErrors(null);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Required by ControlValueAccessor.
   * Function for overriding the `onChange` class method.
   */
  public registerOnChange(fn: (value: any) => void): void {
    // Override the class method with provided function.
    this.onChange = fn;
  }

  /**
   * Required by ControlValueAccessor.
   * Function for overriding the `onTouched` class method.
   */
  public registerOnTouched(fn: () => void): void {
    // Override the class method with provided function.
    this.onTouched = fn;
  }

  /**
   * Required by ControlValueAccessor.
   * Describes how to update the form model when the value changes.
   */
  public writeValue(value: boolean): void {
    // Convert the value to a string.
    // Update the input value.
    this.toggleController.setValue(value, { emitEvent: false });
    this.isChanged$.next(value);
  }


}
